import React, { useState } from 'react';
import { 
  MessageSquare, 
  Phone, 
  Mail, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  HelpCircle,
  Send,
  FileText,
  Download,
  ExternalLink,
  Search,
  Plus
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Alert } from '../ui/Alert';
import { useAuth } from '../../hooks/useAuth';

interface SupportTicket {
  id: string;
  subject: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  last_updated: string;
  description: string;
}

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

export const ClientSupport: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'tickets' | 'faq' | 'contact' | 'resources'>('tickets');
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState<SupportTicket[]>([
    {
      id: 'TKT-001',
      subject: 'Unable to download invoice PDF',
      status: 'resolved',
      priority: 'medium',
      created_at: '2024-01-15T10:30:00Z',
      last_updated: '2024-01-16T14:20:00Z',
      description: 'I am having trouble downloading my invoice PDF. The download button is not working.'
    },
    {
      id: 'TKT-002',
      subject: 'Payment not reflecting in account',
      status: 'in_progress',
      priority: 'high',
      created_at: '2024-01-20T09:15:00Z',
      last_updated: '2024-01-21T11:45:00Z',
      description: 'I made a payment yesterday but it is not showing in my account. Transaction ID: TXN123456'
    }
  ]);

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'How do I pay my invoices?',
      answer: 'You can pay your invoices by clicking the "Pay Now" button on any unpaid invoice. We accept credit cards, bank transfers, UPI, and other payment methods.',
      category: 'Payments'
    },
    {
      id: 'faq-2',
      question: 'How do I download my invoice PDF?',
      answer: 'Click on any invoice to view its details, then click the "PDF" button to download a printable version of your invoice.',
      category: 'Invoices'
    },
    {
      id: 'faq-3',
      question: 'How do I approve or reject a quote?',
      answer: 'Go to the Quotes section, find the quote you want to respond to, and click either "Approve" or "Reject". You can also view the quote details before making a decision.',
      category: 'Quotes'
    },
    {
      id: 'faq-4',
      question: 'Can I update my contact information?',
      answer: 'Yes, you can update your contact information by going to Settings > Profile and editing your details.',
      category: 'Account'
    },
    {
      id: 'faq-5',
      question: 'How do I track my payment history?',
      answer: 'Visit the Payments section to see all your payment transactions, including payment dates, amounts, and methods used.',
      category: 'Payments'
    }
  ];

  const handleCreateTicket = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!ticketForm.subject.trim()) {
        setError('Please enter a subject for your ticket');
        setLoading(false);
        return;
      }

      if (!ticketForm.description.trim()) {
        setError('Please provide a description of your issue');
        setLoading(false);
        return;
      }

      // Mock API call - in real app, this would create a ticket
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Add the new ticket to the list
      const newTicket: SupportTicket = {
        id: `TKT-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
        subject: ticketForm.subject,
        status: 'open',
        priority: ticketForm.priority,
        created_at: new Date().toISOString(),
        last_updated: new Date().toISOString(),
        description: ticketForm.description
      };

      setTickets([newTicket, ...tickets]);
      setSuccess('Support ticket created successfully! We will respond within 24 hours.');
      setTicketForm({ subject: '', priority: 'medium', description: '' });
      
      // Close the form after a delay
      setTimeout(() => {
        setShowCreateTicket(false);
        setSuccess('');
      }, 2000);

    } catch (error: any) {
      setError('Failed to create support ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicketDetails = (ticketId: string) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (ticket) {
      alert(`
Ticket Details:
ID: ${ticket.id}
Subject: ${ticket.subject}
Status: ${ticket.status}
Priority: ${ticket.priority}
Created: ${new Date(ticket.created_at).toLocaleString()}
Last Updated: ${new Date(ticket.last_updated).toLocaleString()}

Description:
${ticket.description}

This would open a detailed view in a real application.
      `);
    }
  };

  const handleDownloadGuide = () => {
    alert('User guide PDF would download in a real application.');
  };

  const handleWatchTutorials = () => {
    alert('This would open a page with video tutorials in a real application.');
  };

  const handleBrowseArticles = () => {
    alert('This would open the knowledge base in a real application.');
  };

  const handleVisitForum = () => {
    alert('This would open the community forum in a real application.');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'in_progress':
        return 'bg-warning-100 text-warning-700 border-warning-200';
      case 'resolved':
        return 'bg-success-100 text-success-700 border-success-200';
      case 'closed':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'urgent':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const filteredFAQs = faqItems.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderTicketsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Support Tickets</h2>
        <Button 
          icon={Plus}
          onClick={() => setShowCreateTicket(true)}
        >
          Create Ticket
        </Button>
      </div>

      {showCreateTicket && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Support Ticket</h3>
          
          {error && <Alert type="error" message={error} />}
          {success && <Alert type="success" message={success} />}

          <div className="space-y-4">
            <FormField label="Subject" required>
              <input
                type="text"
                value={ticketForm.subject}
                onChange={(e) => setTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Brief description of your issue"
              />
            </FormField>

            <FormField label="Priority">
              <select
                value={ticketForm.priority}
                onChange={(e) => setTicketForm(prev => ({ ...prev, priority: e.target.value as any }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </FormField>

            <FormField label="Description" required>
              <textarea
                value={ticketForm.description}
                onChange={(e) => setTicketForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={4}
                placeholder="Please provide detailed information about your issue..."
              />
            </FormField>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="ghost"
                onClick={() => setShowCreateTicket(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                icon={Send}
                onClick={handleCreateTicket}
                loading={loading}
                disabled={loading}
              >
                Create Ticket
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-4">
        {tickets.length === 0 ? (
          <Card className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Support Tickets</h3>
            <p className="text-gray-600 mb-6">You haven't created any support tickets yet.</p>
            <Button 
              icon={MessageSquare}
              onClick={() => setShowCreateTicket(true)}
            >
              Create Your First Ticket
            </Button>
          </Card>
        ) : (
          tickets.map((ticket) => (
            <Card key={ticket.id} hover className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{ticket.subject}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${getStatusColor(ticket.status)}`}>
                      {ticket.status.replace('_', ' ')}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{ticket.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>Ticket #{ticket.id}</span>
                    <span>Created {new Date(ticket.created_at).toLocaleDateString()}</span>
                    <span>Updated {new Date(ticket.last_updated).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleViewTicketDetails(ticket.id)}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );

  const renderFAQTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search FAQs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full text-sm"
          />
        </div>
      </div>

      <div className="space-y-4">
        {filteredFAQs.map((faq) => (
          <Card key={faq.id} className="p-6">
            <div className="flex items-start space-x-3">
              <HelpCircle className="w-6 h-6 text-primary-600 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                    {faq.category}
                  </span>
                </div>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredFAQs.length === 0 && searchTerm && (
        <Card className="text-center py-12">
          <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No FAQs Found</h3>
          <p className="text-gray-600">Try adjusting your search terms or browse all categories.</p>
          <Button 
            variant="primary" 
            className="mt-4"
            onClick={() => setSearchTerm('')}
          >
            Clear Search
          </Button>
        </Card>
      )}
    </div>
  );

  const renderContactTab = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Phone className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Phone Support</h3>
          </div>
          <p className="text-gray-600 mb-3">Call us for immediate assistance</p>
          <p className="text-xl font-semibold text-gray-900 mb-2">+1 (555) 123-4567</p>
          <div className="text-sm text-gray-500">
            <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
            <p>Saturday: 10:00 AM - 4:00 PM</p>
            <p>Sunday: Closed</p>
          </div>
          <Button 
            variant="primary" 
            className="mt-4"
            onClick={() => alert('This would initiate a phone call in a real application.')}
          >
            Call Support
          </Button>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Mail className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Email Support</h3>
          </div>
          <p className="text-gray-600 mb-3">Send us an email for detailed inquiries</p>
          <p className="text-xl font-semibold text-gray-900 mb-2">support@aicrm.com</p>
          <p className="text-sm text-gray-500">We typically respond within 24 hours</p>
          <Button 
            variant="primary" 
            className="mt-4"
            onClick={() => window.location.href = 'mailto:support@aicrm.com'}
          >
            Email Support
          </Button>
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Clock className="w-6 h-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Response Times</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Support Tickets</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• Urgent: Within 2 hours</li>
              <li>• High: Within 4 hours</li>
              <li>• Medium: Within 24 hours</li>
              <li>• Low: Within 48 hours</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Other Channels</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• Phone: Immediate during business hours</li>
              <li>• Email: Within 24 hours</li>
              <li>• Live Chat: Within 5 minutes (when available)</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderResourcesTab = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Help Resources</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">User Guide</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Comprehensive guide covering all features of the client portal
          </p>
          <Button 
            variant="secondary" 
            icon={Download} 
            size="sm"
            onClick={handleDownloadGuide}
          >
            Download PDF Guide
          </Button>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <ExternalLink className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Video Tutorials</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Step-by-step video tutorials for common tasks
          </p>
          <Button 
            variant="secondary" 
            icon={ExternalLink} 
            size="sm"
            onClick={handleWatchTutorials}
          >
            Watch Tutorials
          </Button>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <HelpCircle className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Knowledge Base</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Searchable articles and troubleshooting guides
          </p>
          <Button 
            variant="secondary" 
            icon={ExternalLink} 
            size="sm"
            onClick={handleBrowseArticles}
          >
            Browse Articles
          </Button>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <MessageSquare className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Community Forum</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Connect with other users and share experiences
          </p>
          <Button 
            variant="secondary" 
            icon={ExternalLink} 
            size="sm"
            onClick={handleVisitForum}
          >
            Visit Forum
          </Button>
        </Card>
      </div>

      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="text-blue-900 font-semibold mb-2">Quick Tips</h3>
        <ul className="text-blue-800 text-sm space-y-1">
          <li>• Use the search function to quickly find invoices or quotes</li>
          <li>• Set up email notifications to stay updated on new invoices</li>
          <li>• Download PDFs for your records before making payments</li>
          <li>• Check the FAQ section before creating a support ticket</li>
          <li>• Use the AI Assistant for instant help with common questions</li>
        </ul>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Support Center</h1>
        <p className="text-gray-600 mt-2">Get help and find answers to your questions</p>
      </div>

      {/* Success/Error Alerts */}
      {error && !showCreateTicket && <Alert type="error" message={error} />}
      {success && !showCreateTicket && <Alert type="success" message={success} />}

      {/* Tabs */}
      <Card padding="none">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'tickets', label: 'Support Tickets', icon: MessageSquare },
              { id: 'faq', label: 'FAQ', icon: HelpCircle },
              { id: 'contact', label: 'Contact', icon: Phone },
              { id: 'resources', label: 'Resources', icon: FileText },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'tickets' && renderTicketsTab()}
          {activeTab === 'faq' && renderFAQTab()}
          {activeTab === 'contact' && renderContactTab()}
          {activeTab === 'resources' && renderResourcesTab()}
        </div>
      </Card>
    </div>
  );
};